import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Activity, CheckCircle2, XCircle, MessageSquare, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const ROLE_LABEL = { teacher: "Teacher", coordinator: "Coordinator", admin: "Admin" };
const ROLE_COLOR = { teacher: "var(--ember)", coordinator: "var(--terracotta)", admin: "var(--gold)" };

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function ActionIcon({ action, className = "" }) {
  if (action === "completed") return <CheckCircle2 size={12} className={className} style={{ color: "var(--olive)" }} />;
  if (action === "uncompleted") return <XCircle size={12} className={className} style={{ color: "var(--terracotta)" }} />;
  return <MessageSquare size={12} className={className} style={{ color: "var(--muted)" }} />;
}

function actionLabel(a) {
  if (a === "completed") return "Checked off";
  if (a === "uncompleted") return "Un-checked";
  if (a === "note_updated") return "Added note";
  return a;
}

export default function ActivityLogPanel({ scope, guestId, orgId = null, defaultOpen = false, testid = "guest-activity-panel" }) {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const url = useMemo(
    () => (scope === "coord" ? `/coord/guests/${guestId}/activity` : `/teacher/guests/${guestId}/activity`),
    [scope, guestId],
  );

  const load = () => {
    if (!guestId) return;
    setLoading(true);
    const params = scope === "coord" && orgId ? { org_id: orgId } : {};
    api.get(url, { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, url, orgId]);

  const summary = useMemo(() => {
    const ticks = rows.filter((r) => r.action === "completed").length;
    const notes = rows.filter((r) => r.action === "note_updated").length;
    const uniquePeople = new Set(rows.map((r) => r.by_id).filter(Boolean));
    return { ticks, notes, people: uniquePeople.size };
  }, [rows]);

  return (
    <div className="rounded-md" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }} data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown size={14} style={{ color: "var(--muted)" }} /> : <ChevronRight size={14} style={{ color: "var(--muted)" }} />}
          <Activity size={13} style={{ color: "var(--terracotta)" }} />
          <span className="text-xs uppercase tracking-[0.16em]" style={{ color: "var(--ink-2)" }}>
            Activity log
          </span>
          {rows.length > 0 && (
            <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>
              {summary.ticks} check-off{summary.ticks === 1 ? "" : "s"} · {summary.notes} note{summary.notes === 1 ? "" : "s"} · {summary.people} teammate{summary.people === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {open && (
          <button
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider px-2 py-1 rounded-full hover:opacity-80"
            style={{ background: "var(--surface-hover)", color: "var(--muted)" }}
            data-testid={`${testid}-refresh`}
            title="Refresh"
          >
            <RefreshCw size={10} /> Refresh
          </button>
        )}
      </button>
      {open && (
        <div className="border-t px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
          {loading ? (
            <p className="text-xs" style={{ color: "var(--muted)" }}>Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs italic" style={{ color: "var(--muted)" }} data-testid={`${testid}-empty`}>
              No activity yet. Check-offs and notes on this guest will appear here.
            </p>
          ) : (
            <ul className="space-y-2" data-testid={`${testid}-list`}>
              {rows.map((r) => (
                <li key={r.id} className="flex items-start gap-3 text-xs" data-testid={`${testid}-row-${r.id}`}>
                  <span className="mt-0.5"><ActionIcon action={r.action} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span style={{ color: "var(--ink)", fontWeight: 500 }}>{r.by_name || "Unknown"}</span>
                      <span
                        className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                        style={{ background: "var(--surface-hover)", color: ROLE_COLOR[r.by_role] || "var(--muted)", border: `1px solid ${ROLE_COLOR[r.by_role] || "var(--border-subtle)"}` }}
                      >
                        {ROLE_LABEL[r.by_role] || r.by_role}
                      </span>
                      <span style={{ color: "var(--muted)" }}>{actionLabel(r.action)}</span>
                      <span style={{ color: "var(--ink-2)" }}>
                        {r.track_label} · <em style={{ color: "var(--ink)" }}>{r.item_title}</em>
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 mt-0.5" style={{ color: "var(--muted)" }}>
                      <span className="text-[0.65rem]">{fmtWhen(r.at)}</span>
                      {r.date && r.action === "completed" && (
                        <span className="text-[0.65rem]">Session date: {r.date}</span>
                      )}
                    </div>
                    {r.notes_preview && (
                      <div className="mt-1 pl-2 border-l-2 text-[0.75rem]" style={{ borderColor: "var(--border-strong)", color: "var(--ink-2)" }}>
                        "{r.notes_preview}"
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
